import { ITEM_KINDS, ITEM_STATUSES } from '@updater-core/constants/items';
import optimizely from '@updater-core/interactors/shell/optimizely';
import {
  calculateCost,
  getArrivalTime,
} from '@updater-apps/full-service-move/utils';

const ERROR_DESCRIPTION = {
  minLength: 'Please fill out this field.',
  required: 'Please fill out this field.',
  'pattern.fromAddress.postalCode': 'Please enter a valid Zip code.',
  'pattern.toAddress.postalCode': 'Please enter a valid Zip code.',
};

const EXPERIMENT = 'prs_pod_prs';
const EXPERIMENT_VARIANT = 'variant';

const SUBMIT_CALLS = {
  0: {
    service: 'submitFromAddress',
    getParams: ({
      fromAddress: { street, unit, city, state, postalCode },
    }) => ({
      street,
      unit,
      city,
      state,
      postalCode,
    }),
  },
  1: {
    service: 'submitToAddress',
    getParams: ({ toAddress: { street, unit, city, state, postalCode } }) => ({
      street,
      unit,
      city,
      state,
      postalCode,
    }),
  },
  4: {
    service: 'submitAppointment',
    getParams: ({ fsmAppointmentId, homeSize }) => ({
      id: fsmAppointmentId,
      price: calculateCost(homeSize),
      arrivalWindow: getArrivalTime(fsmAppointmentId),
    }),
  },
};

export function submit({ session, store, fullServiceMoveGateway, moveItems }) {
  const requestHeaders = session.selectors.requestHeaders(session.getState());
  return async () => {
    const currentState = store.getState();
    let result = {};
    const currentIndex = currentState.currentFormIndex || 0;

    const serviceCall = SUBMIT_CALLS[currentIndex];
    if (serviceCall) {
      const params = serviceCall.getParams(currentState);
      result = await fullServiceMoveGateway?.[serviceCall.service]({
        ...params,
        requestHeaders,
      });
      if (serviceCall.service === 'submitAppointment' && !result?.error) {
        markStep({
          store,
          actions: moveItems.actions,
          status: ITEM_STATUSES.COMPLETED,
        })('submit_appointment');
      }
    }

    if (!result?.error) {
      store.setState({
        ...currentState,
        currentFormIndex: currentIndex + 1,
        serverError: undefined,
      });
    } else {
      const serverError = {
        description: result.error,
        timestamp: new Date().getTime(),
      };
      store.setState({
        ...currentState,
        serverError,
      });
    }
    return result;
  };
}

function setProperty(store, response, getProperties) {
  if (response?.error) {
    store.setState({ serverError: response.error, loading: false });
  } else {
    store.setState({ ...getProperties(response), loading: false });
  }
}
export function getAvailableAppointments({
  session,
  store,
  fullServiceMoveGateway,
}) {
  const requestHeaders = session.selectors.requestHeaders(session.getState());
  return async () => {
    store.setState({ loading: true });
    const response = await fullServiceMoveGateway.getAvailableAppointments({
      requestHeaders,
    });

    setProperty(store, response, (data) => ({
      availableAppointments: data,
    }));
  };
}

export function getBookedAppointment({
  store,
  fullServiceMoveGateway,
  session,
}) {
  const requestHeaders = session.selectors.requestHeaders(session.getState());
  return async () => {
    store.setState({ loading: true });
    const bookedAppointmentData =
      await fullServiceMoveGateway.getBookedAppointment({ requestHeaders });

    if (!bookedAppointmentData.error) {
      store.setState({
        ...bookedAppointmentData,
        loading: false,
      });
    }
  };
}

export function init({ store }) {
  return async ({
    currentFormIndex: formIndex,
  }: //   router,
  //   edges,
  {
    currentFormIndex: string | undefined;
  }) => {
    const currentState = store.getState();

    store.setState({
      ...currentState,
      currentFormIndex:
        // if not date selected return to make the selection
        formIndex && parseInt(formIndex) > 2 && !currentState.fsmAppointmentId
          ? 2
          : formIndex,
    });
  };
}

export function update({ store }) {
  return ({ formData }) => {
    const currentState = store.getState();

    store.setState({
      ...currentState,
      ...formData,
    });
  };
}

export function validate() {
  return (formData: object, errors: object) => errors;
}

export function transformErrors() {
  return (errors: any) =>
    errors.map((error: any) => {
      const newDescription =
        ERROR_DESCRIPTION[error.name] ||
        ERROR_DESCRIPTION[`${error.name}${error.property}`];

      const message = newDescription || error.message;
      return {
        ...error,
        message,
      };
    });
}

export function navigateTo({ store }) {
  return (newIndex) => {
    const prevState = store.getState();
    store.setState({
      ...prevState,
      currentFormIndex: newIndex,
      serverError: undefined,
    });
  };
}

export function setEligibleForFsm({ store }) {
  store.setState({
    loadingExperiment: true,
  });
  return async () => {
    const optimizelyExperiment = await optimizely.actions.getVariation(
      EXPERIMENT
    );
    store.setState({
      eligible: optimizelyExperiment === EXPERIMENT_VARIANT,
      loadingExperiment: false,
    });
  };
}

export function markStep({ store, actions, status }) {
  return async (label) => {
    const result = await actions.setStepStatus(
      ITEM_KINDS.RELOCATION_SPECIALISTS,
      status,
      label
    );
    store.setState({
      status,
    });
    return result;
  };
}
